import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
  /**
   * Why the confirm button is dead, in one sentence. Shown beside it and as its
   * title, because a greyed-out action with no explanation is a dead end - on a form
   * of any size the reader is left guessing which field is at fault.
   */
  disabledReason?: string;
  children?: React.ReactNode;
  confirmLabel?: string;
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
};

export const ConfirmDialog = (props: ConfirmDialogProps) => {
  const [confirming, setConfirming] = useState(false);
  const { open, disabled = false, disabledReason, title, description, children } = props;
  const { onConfirm, onCancel, confirmLabel = 'Confirm', confirmVariant = 'default' } = props;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      // Without this, an onConfirm that throws (useClient rethrows every 4xx/5xx
      // after toasting it) left the dialog open with Confirm stuck on "Working…"
      // forever - Cancel was the only way out.
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      {/* A dialog is a panel that floats: the same header strip, hairlines and radius
          as a section panel, so it does not arrive as a different design language on
          top of the one behind it. max-w-2xl rather than 3xl - these are forms of a
          few fields, and 768px of width made two-column reading of a one-column form.
          The body scrolls rather than the dialog growing past a short viewport, which needs the
          content to be a flex column: as the primitive's grid, the body grew instead and the
          footer, Confirm included, was clipped off a dialog taller than 85vh. */}
      <DialogContent className="bg-panel border-line rounded-panel flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-line bg-panel2 shrink-0 space-y-0.5 border-b px-4 py-3 text-left">
          <DialogTitle className="text-foreground text-title font-medium">{title}</DialogTitle>
          {description && <p className="supporting m-0">{description}</p>}
        </DialogHeader>

        {children && <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>}

        <div className="border-line bg-panel2 flex shrink-0 flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-end">
          {!!disabledReason && disabled && !confirming && (
            <span className="supporting sm:mr-auto sm:text-right">{disabledReason}</span>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="border-input h-9 rounded-lg bg-transparent text-body"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={disabled || confirming}
            title={disabled ? disabledReason : undefined}
            className="h-9 rounded-lg text-body"
          >
            {confirming ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
